import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import type { ParkTimeContract } from '@repo/domain';
import { Modal } from '@repo/ui';
import { useMatch, useNavigate } from 'react-router-dom';

import TimeContractPicker from '@shared/components/timeContract/TimeContractPicker';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: TestId;
}

export default function TimeContractPickerModal({ open, onOpenChange, testId }: Props) {
  const navigate = useNavigate();
  // When the picker is opened from the search form (the map route), confirming a
  // custom time also submits the search. Opened from the results/details duration
  // bar, it only updates the time in place.
  const isOnSearchForm = Boolean(useMatch(ROUTE_PATHS[RouteName.MAP]));
  const parkTimeContract = useParkingSearchStore((state) => state.parkTimeContract);
  const setParkUntil = useParkingSearchStore((state) => state.setParkUntil);

  const handleClose = () => onOpenChange(false);

  const handleConfirm = (newContract: ParkTimeContract) => {
    setParkUntil(newContract);
    handleClose();
    if (isOnSearchForm) {
      navigate(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} testId={testId}>
      <div className="mt-3 flex flex-col pb-0.5">
        <div className="text-heading justify-start text-xl leading-7 font-semibold">
          {tr('parking_search:park_duration.custom_time')}
        </div>

        <TimeContractPicker
          contract={parkTimeContract}
          onChange={handleConfirm}
          testId={testId.append('timeContractPicker')}
        />
      </div>
    </Modal>
  );
}
